import type { ContextType } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { PLUGINS } from 'queries/query-keys';
import type {
  RefreshPluginsResult,
  ServerMessage,
} from 'ts-components/PiChat/protocol';
import {
  RESULT_DEADLINE_MARGIN_MS,
  resolveResultUpload,
  type ResolvedResultUpload,
} from 'ts-components/PiChat/presignedPut';
import type { PiChatFrameMeta } from 'ts-components/PiChat/usePiChat';
import type { PluginContext } from 'ts-components/Plugins/context';

export type RefreshPluginsFrame = Extract<
  ServerMessage,
  { type: 'ui.refresh_plugins' }
>;

const LABEL = 'ui.refresh_plugins';
const UNAVAILABLE: RefreshPluginsResult = { ok: false, code: 'unavailable' };

export interface RefreshPluginsDeps {
  refresh(): Promise<unknown>;
  sendResult(
    upload: ResolvedResultUpload<RefreshPluginsResult>,
    result: RefreshPluginsResult
  ): unknown;
}

export interface KiCodeRefreshPlugins {
  handleServerMessage(message: ServerMessage, meta: PiChatFrameMeta): void;
  dispose(): void;
}

export const createKiCodeRefreshPlugins = (
  deps: RefreshPluginsDeps
): KiCodeRefreshPlugins => {
  let disposed = false;
  const pending = new Set<(result: RefreshPluginsResult) => void>();

  const handle = (frame: RefreshPluginsFrame, meta: PiChatFrameMeta) => {
    const upload = resolveResultUpload(
      frame.upload,
      meta.url,
      LABEL,
      UNAVAILABLE
    );
    if (!upload) return;
    const waitMs = upload.expiresAt - RESULT_DEADLINE_MARGIN_MS - Date.now();
    if (disposed || !meta.isCurrent() || waitMs <= 0) {
      deps.sendResult(upload, UNAVAILABLE);
      return;
    }
    const finish = (result: RefreshPluginsResult) => {
      if (!pending.delete(finish)) return;
      clearTimeout(timer);
      deps.sendResult(upload, result);
    };
    const timer = setTimeout(() => finish(UNAVAILABLE), waitMs);
    pending.add(finish);
    deps.refresh().then(
      () => finish({ ok: true }),
      () => finish(UNAVAILABLE)
    );
  };

  return {
    handleServerMessage(message, meta) {
      if (message.type === 'ui.refresh_plugins') handle(message, meta);
    },
    dispose() {
      disposed = true;
      pending.forEach((finish) => finish(UNAVAILABLE));
    },
  };
};

export const refreshKiCodePlugins = (
  {
    refreshPluginArtifacts,
  }: Pick<ContextType<typeof PluginContext>, 'refreshPluginArtifacts'>,
  queryClient: Pick<QueryClient, 'invalidateQueries'>,
  appApiName: string | undefined
) =>
  Promise.all([
    refreshPluginArtifacts(appApiName, { throwOnError: true }),
    queryClient.invalidateQueries(
      { queryKey: PLUGINS.BUSINESS_APPS },
      { throwOnError: true }
    ),
  ]);
